import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, DatePicker, Spin } from 'antd';
import {
  ArrowLeftOutlined,
  CaretRightOutlined,
  FileExcelOutlined,
  ProfileOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  fetchBatchLedger,
  exportBatchLedger,
  type BatchLedgerBatch,
  type BatchLedgerMonth,
  type BatchLedgerRelease,
  type BatchLedgerResponse,
} from '../modules/reports/api/batchLedgerApi';
import { authService } from '../services/auth';
import { formatCurrency } from '../utils/currency';
import { formatDateEuropean } from '../utils/dateUtils';
import { formatProductDimensions } from '../utils/productDimensions';
import { formatUnit } from '../utils/units';
import { formatLocalizedQuantity, localeForLanguage } from '../utils/numberFormatting';
import { formatWarehouseQuantity } from '../modules/warehouse/warehouseQuantityConversion';
import { WAREHOUSE_QUANTITY_PRECISION } from '../utils/warehouseQuantity';
import { useFeedback } from '../utils/feedback';
import { useBackNavigation } from '../hooks/useBackNavigation';
import './BatchLedger.css';

const { RangePicker } = DatePicker;

// Etap 4 batch ledger — the CFO's warehouse ledger.
//
// NAMING: owner decision 2026-08-11 originally named this page "Magazyn"
// (reference spreadsheet "Magazyn Azzura Group"). A same-day addendum
// extended the naming: the stock-levels page (/app/inventory) is now called
// "Magazyn" instead, and THIS page (batch ledger) is now called
// "Księga magazynowa". Every internal identifier stays batchLedger /
// batch-ledger: route, files, i18n KEY names, API module and the backend
// endpoint. Only the translated VALUES changed, so grep still works.
//
// Reads GET /api/reports/batch-ledger through modules/reports/api/batchLedgerApi
// and renders it as a two-sided ledger: receipt (PZ) on the left of the spine,
// every WZ release the batch fed on the right, remaining stock highlighted.
//
// Money rule (owner directive): every figure on this page — per-month subtotals
// and grand totals included — is printed exactly as the server sent it. This
// page must never add money up. If a total is missing, it is a backend change,
// not a reduce() here.

const COLUMN_COUNT = 13;
const EMPTY = '—';

function monthLabel(month: string, language: string): string {
  const parsed = dayjs(`${month}-01`);
  if (!parsed.isValid()) {
    return month;
  }

  return new Intl.DateTimeFormat(localeForLanguage(language), {
    month: 'long',
    year: 'numeric',
  }).format(parsed.toDate());
}

function formatMonthRange(from: string, to: string): string {
  const asLabel = (value: string) => {
    const parsed = dayjs(`${value}-01`);
    return parsed.isValid() ? parsed.format('MM.YYYY') : value;
  };

  return `${asLabel(from)} – ${asLabel(to)}`;
}

/** Money that the server did not send stays absent — never rendered as 0,00 zł. */
function money(value: number | null, currency?: string | null): string {
  if (value === null) {
    return EMPTY;
  }

  return formatCurrency(value, currency || 'PLN');
}

function negativeMoney(value: number | null, currency?: string | null): string {
  if (value === null) {
    return EMPTY;
  }

  return `−${formatCurrency(Math.abs(value), currency || 'PLN')}`;
}

/**
 * Entered-line quantity cell. The figure is always real, but the unit
 * code can be absent on a legacy/manual row — and formatUnit falls back to
 * PIECES for a missing code, which would print a "szt." the data never claimed.
 * So an unlabelled quantity renders as a bare number instead.
 */
function physicalQuantityCell(
  value: number | null,
  unit: string | null,
  language: string,
  signed = false,
): string {
  if (value === null) {
    return EMPTY;
  }

  const magnitude = signed ? Math.abs(value) : value;
  const sign = signed ? '−' : '';

  if (!unit) {
    return `${sign}${formatLocalizedQuantity(magnitude, language, WAREHOUSE_QUANTITY_PRECISION)}`;
  }

  return `${sign}${formatWarehouseQuantity(magnitude, unit, language)}`;
}

/**
 * Unit the unit price is quoted PER. Prefer the server's own `unitPriceUnit`:
 * a dims-DERIVED m²/m³ quantity still carries a per-piece price, so reading
 * the suffix off commercialUnit alone mislabels those rows ("25,00 zł / m²"
 * for a price that is per szt.).
 *
 * The fallback covers a payload predating that field and mirrors the backend
 * rule exactly — commercial basis when there is one, physical otherwise.
 */
function resolveUnitPriceUnit(batch: BatchLedgerBatch): string | null {
  if (batch.unitPriceUnit) {
    return batch.unitPriceUnit;
  }

  return batch.commercialQuantity === null ? batch.physicalUnit : batch.commercialUnit;
}

/**
 * Converted quantity cell. The server is the single home for this
 * pair and sends quantity AND unit together, or neither, whenever there is no
 * genuine equivalent — so a half-present pair is rendered as absent. Nothing
 * is derived here: never a 0, and never an echo of the pieces quantity, which
 * is what this column must not show.
 */
function commercialQuantityCell(
  value: number | null,
  unit: string | null,
  language: string,
  signed = false,
): string {
  if (value === null || !unit) {
    return EMPTY;
  }

  const magnitude = signed ? Math.abs(value) : value;
  return `${signed ? '−' : ''}${formatWarehouseQuantity(magnitude, unit, language)}`;
}

const BatchLedger: React.FC = () => {
  const { t, i18n } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const feedback = useFeedback();
  const language = i18n.language;

  const [data, setData] = useState<BatchLedgerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  // The backend error prose is diagnostic-only and may be English. Keep only
  // the failure state here; the rendered recovery guidance is Polish i18n.
  const [hasLoadError, setHasLoadError] = useState(false);
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(new Set());

  // Defensive guard in addition to the route gate — the ledger is a
  // cross-document financial report and must never render for OPERATIONS.
  useEffect(() => {
    if (!authService.canViewFinancialReports()) {
      navigate('/app/dashboard', { replace: true });
    }
  }, [navigate]);

  const load = useCallback(async (from?: string, to?: string) => {
    setLoading(true);
    setHasLoadError(false);

    try {
      const response = await fetchBatchLedger(from, to);
      setData(response);
      // First load sends no range: adopt the server's default window so the
      // picker and the data always describe the same period.
      if (!from && !to && response.from && response.to) {
        setRange([dayjs(`${response.from}-01`), dayjs(`${response.to}-01`)]);
      }
    } catch {
      setData(null);
      setHasLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRangeChange = (value: [Dayjs | null, Dayjs | null] | null) => {
    if (!value || !value[0] || !value[1]) {
      setRange(null);
      load();
      return;
    }

    const next: [Dayjs, Dayjs] = [value[0], value[1]];
    setRange(next);
    load(next[0].format('YYYY-MM'), next[1].format('YYYY-MM'));
  };

  const handleRefresh = () => {
    if (range) {
      load(range[0].format('YYYY-MM'), range[1].format('YYYY-MM'));
      return;
    }

    load();
  };

  const handleExport = async () => {
    if (!data?.from || !data?.to) {
      return;
    }

    setExporting(true);

    try {
      const result = await exportBatchLedger(data.from, data.to);
      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      feedback.success(t('inventory:batchLedger.export.success'));
    } catch {
      feedback.error(t('inventory:batchLedger.export.error'));
    } finally {
      setExporting(false);
    }
  };

  const toggleMonth = (month: string) => {
    setCollapsedMonths((previous) => {
      const next = new Set(previous);
      if (next.has(month)) {
        next.delete(month);
      } else {
        next.add(month);
      }
      return next;
    });
  };

  const months = useMemo(() => data?.months ?? [], [data]);
  const totals = data?.totals;
  const hasRows = months.some((month) => month.batches.length > 0);

  const renderDocumentLink = (id: string | null, label: string | null, path: string) => {
    if (!label) {
      return <span className="batch-ledger__empty-cell">{EMPTY}</span>;
    }

    if (!id) {
      return <span>{label}</span>;
    }

    return (
      <Link className="batch-ledger__doc" to={`${path}/${id}`}>
        {label}
      </Link>
    );
  };

  const renderRelease = (
    batch: BatchLedgerBatch,
    release: BatchLedgerRelease,
    index: number,
  ) => (
    <tr className="batch-ledger__release" key={`${batch.batchId}-rel-${index}`}>
      <td />
      <td />
      <td>
        <span className="batch-ledger__release-marker">
          <span className="batch-ledger__tick" aria-hidden="true">└</span>
          {release.kind === 'COMPLAINT_WRITE_OFF'
            ? <span data-testid="batch-ledger-complaint-write-off">{t('inventory:batchLedger.rows.complaintWriteOff')}</span>
            : t('inventory:batchLedger.rows.release')}
        </span>
      </td>
      <td className="batch-ledger__num">
        {physicalQuantityCell(release.quantityConsumed, batch.physicalUnit, language, true)}
      </td>
      <td className="batch-ledger__num">
        {commercialQuantityCell(release.commercialQuantityConsumed, batch.commercialUnit, language, true)}
      </td>
      <td />
      <td />
      <td className="batch-ledger__num">{negativeMoney(release.cogsPLN)}</td>
      <td />
      <td className="batch-ledger__date is-spine">{formatDateEuropean(release.releaseDate)}</td>
      <td>{release.kind === 'COMPLAINT_WRITE_OFF'
        ? (release.complaintInvoiceNumber
          ? t('inventory:batchLedger.rows.complaintCorrection', { number: release.complaintInvoiceNumber })
          : <span className="batch-ledger__empty-cell">{EMPTY}</span>)
        : renderDocumentLink(release.wzDocumentId, release.wzNumber, '/app/wz')}</td>
      <td title={release.salesInvoiceNumber || undefined}>
        {release.salesInvoiceNumber || (
          <span className="batch-ledger__empty-cell">{EMPTY}</span>
        )}
      </td>
      <td title={release.customerName || undefined}>
        {release.customerName || <span className="batch-ledger__empty-cell">{EMPTY}</span>}
      </td>
    </tr>
  );

  const renderRemainder = (batch: BatchLedgerBatch) => (
    <tr className="batch-ledger__remainder" key={`${batch.batchId}-remainder`}>
      <td />
      <td />
      <td>
        <span className="batch-ledger__remainder-label">
          <span className="batch-ledger__tick" aria-hidden="true">└</span>
          {t('inventory:batchLedger.rows.remaining')}
        </span>
      </td>
      <td className="batch-ledger__num">
        {physicalQuantityCell(batch.quantityRemaining, batch.physicalUnit, language)}
      </td>
      <td className="batch-ledger__num">
        {commercialQuantityCell(
          batch.commercialQuantityRemaining ?? null,
          batch.commercialUnit,
          language,
        )}
      </td>
      <td />
      <td />
      <td className="batch-ledger__num">{money(batch.remainingValuePLN)}</td>
      <td />
      <td className="is-spine" colSpan={4} />
    </tr>
  );

  const renderBatch = (batch: BatchLedgerBatch, isFirst: boolean) => {
    const inStock = batch.quantityRemaining > 0;
    const dimensions = formatProductDimensions(batch.dimensions, { language });
    const unitPriceUnit = resolveUnitPriceUnit(batch);
    const rowClasses = [
      'batch-ledger__batch',
      inStock ? 'batch-ledger__batch--instock' : '',
      isFirst ? '' : 'batch-ledger__batch--separated',
    ].filter(Boolean).join(' ');

    return (
      <tr className={rowClasses} key={batch.batchId} data-testid={`batch-row-${batch.batchId}`}>
        <td className="batch-ledger__date">{formatDateEuropean(batch.receiptDate)}</td>
        <td>
          {renderDocumentLink(batch.pzDocumentId, batch.pzNumber, '/app/pz')}
          {batch.supplierName && (
            <span className="batch-ledger__supplier" title={batch.supplierName}>{batch.supplierName}</span>
          )}
        </td>
        <td>
          <span className="batch-ledger__product">
            <span className="batch-ledger__product-name" title={batch.productName || undefined}>
              {batch.productName || t('inventory:batchLedger.rows.unnamedProduct')}
            </span>
            {dimensions && <span className="batch-ledger__product-meta" title={dimensions}>{dimensions}</span>}
          </span>
        </td>
        <td className="batch-ledger__num batch-ledger__num--strong">
          {physicalQuantityCell(batch.physicalQuantity, batch.physicalUnit, language)}
        </td>
        <td className="batch-ledger__num">
          {commercialQuantityCell(batch.commercialQuantity, batch.commercialUnit, language)}
        </td>
        <td className="batch-ledger__num">
          {money(batch.unitPrice, batch.currency)}
          {batch.unitPrice !== null && unitPriceUnit && (
            <span className="batch-ledger__product-meta">
              {` / ${formatUnit(unitPriceUnit, language)}`}
            </span>
          )}
        </td>
        <td className="batch-ledger__num">{money(batch.valueOriginal, batch.currency)}</td>
        <td className="batch-ledger__num batch-ledger__num--strong">
          {money(batch.valuePLN)}
          {batch.capitalizedExtraPLN !== null && batch.capitalizedExtraPLN > 0 && (
            <span
              className="batch-ledger__value-note"
              title={`${t('inventory:batchLedger.rows.capitalizedExtra')} ${money(batch.capitalizedExtraPLN)}`}
            >
              {`${t('inventory:batchLedger.rows.capitalizedExtraShort')} ${money(batch.capitalizedExtraPLN)}`}
            </span>
          )}
        </td>
        <td title={batch.supplierInvoiceNumber || undefined}>
          {batch.supplierInvoiceNumber || (
            <span className="batch-ledger__empty-cell">{EMPTY}</span>
          )}
        </td>
        <td className="is-spine" colSpan={4} />
      </tr>
    );
  };

  const renderMonth = (month: BatchLedgerMonth) => {
    const collapsed = collapsedMonths.has(month.month);

    return (
      <tbody className="batch-ledger__month" key={month.month}>
        <tr className="batch-ledger__band">
          <td colSpan={COLUMN_COUNT}>
            <button
              type="button"
              className="batch-ledger__band-inner"
              aria-expanded={!collapsed}
              onClick={() => toggleMonth(month.month)}
            >
              <CaretRightOutlined className="batch-ledger__band-chevron" aria-hidden="true" />
              <span className="batch-ledger__band-name">
                {monthLabel(month.month, language)}
              </span>
              <span className="batch-ledger__band-facts">
                <span className="batch-ledger__band-fact">
                  {t('inventory:batchLedger.summary.batches')}
                  <b>{month.summary.batchCount}</b>
                </span>
                <span className="batch-ledger__band-fact">
                  {t('inventory:batchLedger.summary.received')}
                  <b>{formatCurrency(month.summary.totalValuePLN, 'PLN')}</b>
                </span>
                <span className="batch-ledger__band-fact batch-ledger__band-fact--stock">
                  {t('inventory:batchLedger.summary.inStock')}
                  <b>{formatCurrency(month.summary.remainingValuePLN, 'PLN')}</b>
                </span>
              </span>
            </button>
          </td>
        </tr>

        {!collapsed && month.batches.map((batch, index) => {
          const rows: React.ReactNode[] = [renderBatch(batch, index === 0)];
          batch.releases.forEach((release, releaseIndex) => {
            rows.push(renderRelease(batch, release, releaseIndex));
          });
          if (batch.quantityRemaining > 0) {
            rows.push(renderRemainder(batch));
          }
          return rows;
        })}
      </tbody>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="batch-ledger__state">
          <Spin />
          <span className="batch-ledger__state-body">
            {t('inventory:batchLedger.loading')}
          </span>
        </div>
      );
    }

    if (hasLoadError) {
      return (
        <div className="batch-ledger__state">
          <Alert
            type="error"
            showIcon
            style={{ textAlign: 'left', maxWidth: 560 }}
            message={t('inventory:batchLedger.error.title')}
            description={(
              <>
                <div>{t('inventory:batchLedger.error.body')}</div>
                <div style={{ marginTop: 8, opacity: 0.75 }}>{t('inventory:batchLedger.error.unknown')}</div>
              </>
            )}
            action={(
              <Button size="small" icon={<ReloadOutlined />} onClick={handleRefresh}>
                {t('inventory:batchLedger.actions.retry')}
              </Button>
            )}
          />
        </div>
      );
    }

    if (!hasRows) {
      return (
        <div className="batch-ledger__state">
          <ProfileOutlined className="batch-ledger__state-icon" aria-hidden="true" />
          <span className="batch-ledger__state-title">
            {t('inventory:batchLedger.empty.title')}
          </span>
          <span className="batch-ledger__state-body">
            {t('inventory:batchLedger.empty.body')}
          </span>
        </div>
      );
    }

    return (
      <div className="batch-ledger__scroll">
        <table className="batch-ledger__table">
          {/* Fixed layout: the widths sum to 1 400 px, so the whole ledger fits a
              16-inch screen; long names end with an ellipsis (full text in title). */}
          <colgroup>
            <col style={{ width: 90 }} />
            <col style={{ width: 132 }} />
            <col style={{ width: 162 }} />
            <col style={{ width: 84 }} />
            <col style={{ width: 96 }} />
            <col style={{ width: 106 }} />
            <col style={{ width: 104 }} />
            <col style={{ width: 112 }} />
            <col style={{ width: 104 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 104 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 96 }} />
          </colgroup>

          <thead>
            <tr className="batch-ledger__sides">
              <th colSpan={9} scope="colgroup">
                {t('inventory:batchLedger.sides.receipt')}
              </th>
              <th colSpan={4} scope="colgroup" className="is-spine">
                {t('inventory:batchLedger.sides.release')}
              </th>
            </tr>
            <tr className="batch-ledger__heads">
              <th scope="col" title={t('inventory:batchLedger.columns.receiptDate')}>{t('inventory:batchLedger.columns.receiptDate')}</th>
              <th scope="col" title={t('inventory:batchLedger.columns.pz')}>{t('inventory:batchLedger.columns.pz')}</th>
              <th scope="col" title={t('inventory:batchLedger.columns.product')}>{t('inventory:batchLedger.columns.product')}</th>
              <th scope="col" className="batch-ledger__num" title={t('inventory:batchLedger.columns.enteredQuantity')}>
                {t('inventory:batchLedger.columns.enteredQuantity')}
              </th>
              <th scope="col" className="batch-ledger__num" title={t('inventory:batchLedger.columns.convertedQuantity')}>
                {t('inventory:batchLedger.columns.convertedQuantity')}
              </th>
              <th scope="col" className="batch-ledger__num" title={t('inventory:batchLedger.columns.unitPrice')}>
                {t('inventory:batchLedger.columns.unitPrice')}
              </th>
              <th scope="col" className="batch-ledger__num" title={t('inventory:batchLedger.columns.value')}>
                {t('inventory:batchLedger.columns.value')}
              </th>
              <th scope="col" className="batch-ledger__num" title={t('inventory:batchLedger.columns.valuePLN')}>
                {t('inventory:batchLedger.columns.valuePLN')}
              </th>
              <th scope="col" title={t('inventory:batchLedger.columns.purchaseInvoice')}>{t('inventory:batchLedger.columns.purchaseInvoice')}</th>
              <th scope="col" className="is-spine" title={t('inventory:batchLedger.columns.releaseDate')}>
                {t('inventory:batchLedger.columns.releaseDate')}
              </th>
              <th scope="col" title={t('inventory:batchLedger.columns.wz')}>{t('inventory:batchLedger.columns.wz')}</th>
              <th scope="col" title={t('inventory:batchLedger.columns.salesInvoice')}>{t('inventory:batchLedger.columns.salesInvoice')}</th>
              <th scope="col" title={t('inventory:batchLedger.columns.customer')}>{t('inventory:batchLedger.columns.customer')}</th>
            </tr>
          </thead>

          {months.map(renderMonth)}

          {totals && (
            <tfoot>
              <tr className="batch-ledger__grand">
                <td colSpan={7} className="batch-ledger__grand-label">
                  {t('inventory:batchLedger.totals.label')}
                  {/* Count rendered as a labelled figure, never inflected into
                      the sentence — Polish needs partie/partii by quantity. */}
                  <span className="batch-ledger__grand-count">
                    {`${t('inventory:batchLedger.summary.batches')} ${totals.batchCount}`}
                  </span>
                </td>
                <td className="batch-ledger__num">
                  {formatCurrency(totals.totalValuePLN, 'PLN')}
                </td>
                <td />
                <td colSpan={4} className="is-spine batch-ledger__grand-stock">
                  {`${t('inventory:batchLedger.totals.inStock')} ${formatCurrency(totals.remainingValuePLN, 'PLN')}`}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  };

  return (
    <div className="batch-ledger-page">
      <div className="batch-ledger__masthead">
        {/* No <h1> here: Layout.tsx already renders "Księga magazynowa" in the
            sticky header from PAGE_TITLE_KEYS. This is the standfirst only. */}
        <div className="batch-ledger__titles">
          <span className="batch-ledger__eyebrow">{t('inventory:batchLedger.eyebrow')}</span>
          <span className="batch-ledger__lede">{t('inventory:batchLedger.lede')}</span>
        </div>

        {totals && !hasLoadError && (
          <dl className="batch-ledger__totals">
            <div className="batch-ledger__total">
              <dt>{t('inventory:batchLedger.summary.batches')}</dt>
              <dd>{totals.batchCount}</dd>
            </div>
            <div className="batch-ledger__total">
              <dt>{t('inventory:batchLedger.summary.received')}</dt>
              <dd>{formatCurrency(totals.totalValuePLN, 'PLN')}</dd>
            </div>
            <div className="batch-ledger__total batch-ledger__total--stock">
              <dt>{t('inventory:batchLedger.summary.inStock')}</dt>
              <dd>{formatCurrency(totals.remainingValuePLN, 'PLN')}</dd>
            </div>
          </dl>
        )}
      </div>

      <div className="batch-ledger__controls">
        <Button icon={<ArrowLeftOutlined />} onClick={() => goBack('/app/inventory')}>
          {t('common:back')}
        </Button>

        <div className="batch-ledger__field">
          <span className="batch-ledger__field-label">
            {t('inventory:batchLedger.filters.range')}
          </span>
          <RangePicker
            picker="month"
            allowClear
            value={range}
            onChange={handleRangeChange}
            format="MM.YYYY"
          />
        </div>

        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
          {t('inventory:batchLedger.actions.refresh')}
        </Button>

        <Button
          type="primary"
          icon={<FileExcelOutlined />}
          onClick={handleExport}
          loading={exporting}
          disabled={loading || hasLoadError || !data?.from || !data?.to}
        >
          {t('inventory:batchLedger.actions.exportXlsx')}
        </Button>

        {data?.from && data?.to && (
          <span className="batch-ledger__range-note">
            {`${t('inventory:batchLedger.filters.showing')} ${formatMonthRange(data.from, data.to)}`}
          </span>
        )}
      </div>

      <div className="batch-ledger__sheet">{renderBody()}</div>

      {hasRows && !loading && !hasLoadError && (
        <div className="batch-ledger__legend">
          <span className="batch-ledger__legend-item">
            <span className="batch-ledger__legend-swatch batch-ledger__legend-swatch--stock" />
            {t('inventory:batchLedger.legend.remaining')}
          </span>
          <span className="batch-ledger__legend-item">
            <span className="batch-ledger__legend-swatch batch-ledger__legend-swatch--out">−</span>
            {t('inventory:batchLedger.legend.release')}
          </span>
          <span className="batch-ledger__legend-item">
            {t('inventory:batchLedger.legend.collapse')}
          </span>
        </div>
      )}
    </div>
  );
};

export default BatchLedger;
